import React, { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  Settings, 
  Upload,
  Calculator,
  GitCompare,
  Building2,
  Database,
  BookOpen,
  LogOut,
  UserCog,
  Shield,
  PlusCircle,
  Key
} from "lucide-react";

// Context
import { ClientProvider } from "@/context/ClientContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ClientSelector from "@/components/ClientSelector";
import ChangePasswordModal from "@/components/ChangePasswordModal";

// Pages
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import PartyLedgers from "@/pages/PartyLedgers";
import TDSTransactions from "@/pages/TDSTransactions";
import TDSSummary from "@/pages/TDSSummary";
import Reconciliation from "@/pages/Reconciliation";
import ConnectionSettings from "@/pages/ConnectionSettings";
import Clients from "@/pages/Clients";
import TDSImport from "@/pages/TDSImport";
import Tutorial from "@/pages/Tutorial";
import UserManagement from "@/pages/UserManagement";
import ManualEntry from "@/pages/ManualEntry";
import ClientManagement from "@/pages/ClientManagement";

// Static role arrays (hoisted to avoid re-creation on every render)
const ROLES_ADMIN_EMPLOYEE = ['admin', 'employee'];
const ROLES_ADMIN_ONLY = ['admin'];

// Protected Route component
const ProtectedRoute = ({ children, allowedRoles = null }) => {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Check role-based access if specified
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-slate-500">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }
  
  return children;
};

const Sidebar = () => {
  const { user, logout, isAdmin, canEdit, canViewOnly } = useAuth();
  const [showChangePassword, setShowChangePassword] = useState(false);
  
  // Define nav items based on role
  const getNavItems = () => {
    const baseItems = [
      { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard", roles: ['admin', 'employee', 'customer'] },
      { path: "/transactions", icon: FileText, label: "TDS Transactions", roles: ['admin', 'employee', 'customer'] },
      { path: "/summary", icon: Calculator, label: "TDS Summary", roles: ['admin', 'employee', 'customer'] },
    ];
    
    // Items only for admin and employee
    const editItems = [
      { path: "/tds-import", icon: Upload, label: "TDS Import", roles: ['admin', 'employee'] },
      { path: "/manual-entry", icon: PlusCircle, label: "Manual Entry", roles: ['admin', 'employee'] },
      { path: "/ledgers", icon: Users, label: "Party Ledgers", roles: ['admin', 'employee'] },
      { path: "/reconciliation", icon: GitCompare, label: "Reconciliation", roles: ['admin', 'employee'] },
      { path: "/clients", icon: Building2, label: "Clients", roles: ['admin', 'employee'] },
    ];
    
    // Admin only items
    const adminItems = [
      { path: "/users", icon: UserCog, label: "User Management", roles: ['admin'] },
      { path: "/client-management", icon: Building2, label: "Client Management", roles: ['admin'] },
      { path: "/settings", icon: Settings, label: "Settings", roles: ['admin', 'employee'] },
    ];
    
    // Tutorial for all
    const tutorialItem = { path: "/tutorial", icon: BookOpen, label: "Tutorial", roles: ['admin', 'employee', 'customer'] };
    
    // Filter based on user role
    const userRole = user?.role || 'customer';
    return [...baseItems, ...editItems, ...adminItems, tutorialItem].filter(item => 
      item.roles.includes(userRole)
    );
  };

  const navItems = getNavItems();

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  const getRoleBadge = () => {
    const role = user?.role;
    const config = {
      admin: { bg: 'bg-red-100', text: 'text-red-700', label: 'Admin' },
      employee: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Employee' },
      customer: { bg: 'bg-green-100', text: 'text-green-700', label: 'Customer' }
    };
    const cfg = config[role] || config.customer;
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text}`}>
        {cfg.label}
      </span>
    );
  };

  return (
    <aside className="sidebar" data-testid="sidebar">
      <div className="sidebar-header">
        <div className="flex items-center gap-3">
          <img 
            src="/finominal-logo.jpg" 
            alt="Finominal Logo" 
            className="h-10 w-auto rounded"
          />
          <div>
            <h1 className="font-heading font-bold text-slate-900 text-lg">TDS Master</h1>
            <p className="text-xs text-slate-500">by Finominal</p>
          </div>
        </div>
      </div>
      
      {/* User Info */}
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-900 truncate">{user?.full_name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
          {getRoleBadge()}
        </div>
      </div>
      
      {/* Client Selector - All authenticated users need to see/switch clients */}
      <div className="px-4 py-3 border-b border-slate-100">
        <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Current Client</p>
        <ClientSelector />
      </div>
      
      <nav className="sidebar-nav flex-1">
        <ul className="space-y-1">
          {navItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  `nav-link ${isActive ? 'active' : ''}`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      
      {/* User Actions */}
      <div className="px-4 py-3 border-t border-slate-100 space-y-1">
        <button
          onClick={() => setShowChangePassword(true)}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          data-testid="change-password-btn"
        >
          <Key className="w-4 h-4" />
          Change Password
        </button>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
      </div>
      
      {/* Change Password Modal */}
      <ChangePasswordModal open={showChangePassword} onOpenChange={setShowChangePassword} />
    </aside>
  );
};

// Main Layout with Sidebar
const MainLayout = ({ children }) => {
  return (
    <div className="app-container">
      <Sidebar />
      <main className="main-content">
        {children}
      </main>
    </div>
  );
};

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-100">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/login" 
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} 
      />
      
      <Route 
        path="/reset-password" 
        element={<ResetPassword />} 
      />
      
      {/* Protected routes */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <MainLayout><Dashboard /></MainLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/tds-import" 
        element={
          <ProtectedRoute allowedRoles={ROLES_ADMIN_EMPLOYEE}>
            <MainLayout><TDSImport /></MainLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/manual-entry" 
        element={
          <ProtectedRoute allowedRoles={ROLES_ADMIN_EMPLOYEE}>
            <MainLayout><ManualEntry /></MainLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/ledgers" 
        element={
          <ProtectedRoute allowedRoles={ROLES_ADMIN_EMPLOYEE}>
            <MainLayout><PartyLedgers /></MainLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/transactions" 
        element={
          <ProtectedRoute>
            <MainLayout><TDSTransactions /></MainLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/summary" 
        element={
          <ProtectedRoute>
            <MainLayout><TDSSummary /></MainLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/reconciliation" 
        element={
          <ProtectedRoute allowedRoles={ROLES_ADMIN_EMPLOYEE}>
            <MainLayout><Reconciliation /></MainLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/clients" 
        element={
          <ProtectedRoute allowedRoles={ROLES_ADMIN_EMPLOYEE}>
            <MainLayout><Clients /></MainLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/users" 
        element={
          <ProtectedRoute allowedRoles={ROLES_ADMIN_ONLY}>
            <MainLayout><UserManagement /></MainLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/client-management" 
        element={
          <ProtectedRoute allowedRoles={ROLES_ADMIN_ONLY}>
            <MainLayout><ClientManagement /></MainLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/tutorial" 
        element={
          <ProtectedRoute>
            <MainLayout><Tutorial /></MainLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute allowedRoles={ROLES_ADMIN_EMPLOYEE}>
            <MainLayout><ConnectionSettings /></MainLayout>
          </ProtectedRoute>
        } 
      />
      
      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <ClientProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </ClientProvider>
    </AuthProvider>
  );
}

export default App;
